import { useState } from 'react';
import { quoteCanEdit } from '../lib/quoteRevision.js';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Copy, Download, Eye, History, Pencil, Send, Timer, Trash2 } from 'lucide-react';
import type { Quote } from '@worki/shared';
import { api, RequestError } from '../lib/api.js';
import { publicOrigin } from '../lib/config.js';
import { useAuthenticated } from '../lib/session.js';
import * as platform from '@platform';
import { currentLocale } from '../i18n.js';
import { formatDateTime, formatDuration, money } from '../lib/format.js';
import { sendActionOf } from '../lib/sendAction.js';
import { ErrorNotice, LoadingScreen } from '../components/ui.js';
import { ConfirmDialog } from '../components/Modal.js';
import { ReminderDialog } from '../components/ReminderDialog.js';
import { SendQuoteDialog } from '../components/SendQuoteDialog.js';
import { SendHistoryDialog } from '../components/SendHistoryDialog.js';
import { QuoteDocument, quoteToDoc } from '../components/QuoteDocument.js';
import { QuotePdfDownloadButton } from '../components/QuotePdfDownloadButton.js';
import { useToast } from '../components/Toast.js';
import { cn } from '../lib/cn.js';

const STATUS_CLASS: Record<Quote['status'], string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-info/10 text-info',
  approved: 'bg-success/10 text-success',
  rejected: 'bg-destructive/10 text-destructive',
  expired: 'bg-warning/10 text-warning',
};

/** QUO-006..011 — the owner's view of a single quote. */
export function QuoteDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const locale = currentLocale();
  const session = useAuthenticated();

  const [sendOpen, setSendOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const quoteQuery = useQuery({
    queryKey: ['quote', id],
    queryFn: () => api.get<Quote>(`/quotes/${id}`),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/quotes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotes'] });
      void queryClient.invalidateQueries({ queryKey: ['history'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/quotes', { replace: true });
    },
    onError: (error: unknown) => {
      setConfirmDelete(false);
      toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown'));
    },
  });

  if (quoteQuery.isError) return <ErrorNotice code={quoteQuery.error instanceof RequestError ? quoteQuery.error.code : 'errors.unknown'} />;
  if (quoteQuery.isLoading || !quoteQuery.data) return <LoadingScreen />;
  const quote = quoteQuery.data;

  const publicLink = quote.publicToken && !quote.isLocked
    ? `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/q/${quote.publicToken}`
    : null;
  // HIST 2.8 / 2.10 — the list's rule: an approved quote is sent as a copy, and
  // a rejected or lapsed one is not sent at all.
  const send = sendActionOf('quote', quote);

  return (
    <div className="animate-fade-up max-w-2xl mx-auto space-y-3">
      {/* What the screen is about: the number, where the quote stands, what it
          is for and the total being argued over. */}
      <section className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold ltr-nums">
            {t('quotes.numberShort', { number: quote.number })}
          </span>
          <span
            className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_CLASS[quote.status])}
          >
            {t(`enum.quoteStatus.${quote.status}`)}
          </span>
        </div>
        <p className="text-sm mt-2 line-clamp-2">{quote.title}</p>
        {/* The name is what the quote was written to, not a link onwards —
            there is no customer screen behind it any more. */}
        <p className="text-xs text-muted-foreground">{quote.customer?.name}</p>
        <p className="text-2xl font-bold text-primary mt-2 ltr-nums">
          {money(quote.totals.total, quote.currency, locale)}
        </p>
      </section>

      {/* Three across rather than five: a sent quote now has six actions, and
          five columns left the sixth alone on a row of its own. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {send && (
          <ActionTile icon={<Send className="h-4 w-4" />} onClick={() => setSendOpen(true)}>
            {send === 'copy' ? t('history.sendCopy') : send === 'resend' ? t('quotes.sendAgain') : t('quotes.sendQuote')}
          </ActionTile>
        )}
        <QuotePdfDownloadButton quoteId={quote.id} quoteNumber={quote.number} className="glass-card rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 text-center text-[11px] font-medium hover:border-primary/30 transition-colors active:scale-[0.97]">
          <Download className="h-4 w-4 text-primary" />
        </QuotePdfDownloadButton>
        <ActionTile icon={<Bell className="h-4 w-4" />} onClick={() => setReminderOpen(true)}>
          {t('quotes.createReminder')}
        </ActionTile>
        {/* QUO-021 — only once something has gone out. `sentAt` is the first
            send; the dialog lists every one of them. */}
        {quote.sentAt && (
          <ActionTile icon={<History className="h-4 w-4" />} onClick={() => setHistoryOpen(true)}>
            {t('quotes.sendHistory')}
          </ActionTile>
        )}
        {quoteCanEdit(quote) && (
          <ActionTile icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/quotes/${quote.id}/edit`)}>
            {t('common.edit')}
          </ActionTile>
        )}
        {publicLink && (
          <ActionTile
            icon={<Eye className="h-4 w-4" />}
            onClick={() => void platform.openExternal(publicLink)}
          >
            {t('quotes.openPublicLink')}
          </ActionTile>
        )}
        <ActionTile danger icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmDelete(true)}>
          {t('common.delete')}
        </ActionTile>
      </div>
      {quote.isLocked && <p className="text-xs text-muted-foreground">{t('errors.quoteLocked')}</p>}

      {/* The link is read-only text that gets copied, so it is text with a copy
          button — not a text field pretending to be editable. */}
      {publicLink && (
        <div className="glass-card rounded-xl px-3 py-2.5 space-y-1">
          <div className="flex items-center gap-2">
            <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate" dir="ltr">
              {publicLink}
            </span>
            <button
              type="button"
              aria-label={t('common.copy')}
              className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/15 transition-colors"
              onClick={() => {
                void (async () => {
                  const copied = await platform.copyText(publicLink);
                  if (copied) toast.success(t('common.copied'));
                  else toast.error(t('errors.unknown'));
                })();
              }}
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* QUO-010 — view tracking. */}
      {quote.viewCount === 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground px-1">
          <Timer className="h-3.5 w-3.5" />
          {t('quotes.notViewedYet')}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label={t('quotes.viewCount')} value={String(quote.viewCount)} />
          <Stat label={t('quotes.viewDuration')} value={formatDuration(quote.totalViewSeconds)} />
          <Stat label={t('quotes.firstViewed')} value={formatDateTime(quote.firstViewedAt, locale)} />
          <Stat label={t('quotes.lastViewed')} value={formatDateTime(quote.lastViewedAt, locale)} />
        </div>
      )}

      {quote.status === 'rejected' && quote.rejectionReason && (
        <div className="glass-card rounded-xl p-4 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground">{t('quotes.rejectionReason')}</p>
          <p className="text-sm font-medium">{t(`enum.rejectionReason.${quote.rejectionReason}`)}</p>
          {quote.rejectionNote && <p className="text-xs text-muted-foreground">{quote.rejectionNote}</p>}
          <p className="text-[11px] text-muted-foreground ltr-nums">
            {formatDateTime(quote.rejectedAt, locale)}
          </p>
        </div>
      )}

      {quote.status === 'approved' && (
        <div className="glass-card rounded-xl p-4 flex items-center gap-2 text-sm">
          <span className="text-success">✅</span>
          <span>{t('quotes.approvedAt')}</span>
          <strong className="ltr-nums">{formatDateTime(quote.approvedAt, locale)}</strong>
        </div>
      )}

      {/* The quote itself, exactly as the customer reads it — one renderer for
          the list preview, this screen and the editor's preview. */}
      <section className="glass-card rounded-xl overflow-hidden">
        <QuoteDocument doc={quoteToDoc(quote, session.business, session.features.quoteLinkTtlDays)} />
      </section>

      <SendHistoryDialog
        open={historyOpen}
        quoteId={quote.id}
        onClose={() => setHistoryOpen(false)}
        {...(send
          ? {
              onResend: () => setSendOpen(true),
              ...(send === 'copy' ? { resendLabel: t('history.sendCopy') } : {}),
            }
          : {})}
      />

      <SendQuoteDialog
        open={sendOpen}
        quote={quote}
        onClose={() => setSendOpen(false)}
        onSent={() => { void queryClient.invalidateQueries({ queryKey: ['quote', id] }); navigate('/quotes'); }}
      />

      <ReminderDialog
        open={reminderOpen}
        onClose={() => setReminderOpen(false)}
        source="quote"
        sourceId={quote.id}
        defaultTitle={t('quotes.numberShort', { number: quote.number })}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('common.delete')}
        message={t('quotes.deleteConfirm')}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}

/** One action, as a tile: an icon over a word. Renders as a link when given one. */
function ActionTile({
  icon,
  children,
  onClick,
  href,
  danger = false,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  danger?: boolean;
}) {
  const className = cn(
    'glass-card rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 text-center',
    'text-[11px] font-medium hover:border-primary/30 transition-colors active:scale-[0.97]',
    danger ? 'text-destructive' : 'text-foreground',
  );

  if (href) {
    return (
      <a className={className} href={href} target="_blank" rel="noreferrer">
        <span className={danger ? 'text-destructive' : 'text-primary'}>{icon}</span>
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={onClick}>
      <span className={danger ? 'text-destructive' : 'text-primary'}>{icon}</span>
      {children}
    </button>
  );
}

/** The four tracking figures, as the dashboard's tiles. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="glass-card rounded-xl p-3 text-center">
      <span className="block text-sm font-semibold ltr-nums">{value}</span>
      <span className="block text-[11px] text-muted-foreground mt-0.5">{label}</span>
    </div>
  );
}
